import { useEffect, useState, useCallback } from 'react';
import { set, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as schemas from '../../../../assets/schemas/schemas';
import { CashTable } from '../../../common/Tables/Tables';
import { ChangeTableHeader } from '../../../../assets/helpers/tables.js';
import { EditChangeModal } from '../../../common/Modals/Modals';
import { paymentsService } from '../../../../services/paymentsService';
import { rateService } from '../../../../services/rateService';
import { productService } from '../../../../services/productService';
import { notify } from '../../../../assets/helpers/toast';
import { LoadSpinner } from '../../../common/LoadSpinner/LoadSpinner';
import { OderCardHeader } from './OrderComponents';
import { useDropzone } from 'react-dropzone';
import {
  paymentType,
  paymentTypeOptions as paymentTypesOptions,
} from '../../../../assets/helpers/options';
import authService from '../../../../services/authService';
import { currencyRateService } from '../../../../services/currencyRateService.js';

import './OrderComponent.scss';
import moment from 'moment';

import { salesRoles } from '../../../../assets/helpers/roles';

export const ChangeCard = (props) => {
  const { label, membership, reloadMembership } = props;
  const [open, setOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [openEditPaymentModal, setOpenEditPaymentModal] = useState({
    isOpen: false,
    payment: '',
  });
  const [dollarRate, setDollarRate] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
    getValues,
  } = useForm({
    resolver: yupResolver(schemas.editCashSchema()),
  });

  const getDollarRate = async () => {
    rateService
      .getDollarRate()
      .then((response) => {
        if (response.status === 200) {
          setDollarRate(response?.data?.rate);
        } else {
          notify(`${response.message}` || 'error', 'error');
        }
      })
      .catch(() => {});
  };

  const getPaymentMethods = () => {
    productService
      .getPaymentMethods('sales')
      .then((response) => {
        if (response.status === 200) {
          setPaymentMethods(response?.data);
        } else {
          notify(`${response.message}`, 'error');
        }
      })
      .catch(() => {});
  };

  const getDollarRateByDate = (date) => {
    const params = {
      currency: 'USD',
      date: moment(date).format('YYYY-MM-DD'),
      limit: 2,
    };

    currencyRateService
      .getCurrencyRates(params)
      .then((response) => {
        if (response.status === 200) {
          setValue(
            'dollarRateToCalculate',
            response?.data?.result[0]?.rate ?? 0
          );
        } else {
          notify(`${response.message}` || 'error', 'error');
        }
      })
      .catch((error) => {
        console.error('!! error', error);
        notify(`Error al obtener la tasa del día`, 'error');
      });
    return true;
  };

  const handleEditChangeModal = async (payment, membership) => {
    setOpenEditPaymentModal((prevState) => {
      return {
        ...prevState,
        isOpen: !openEditPaymentModal?.isOpen,
        payment: payment ? payment : '',
      };
    });

    if (payment) {
      await getDollarRateByDate(membership?.createdAt);
      setValue('totalPaid', membership?.totalPaid);
      setValue('paid', payment?.paid);
      setValue('amount', payment?.amount);
      setValue('amountBs', payment?.amountBs);
      setValue('method', payment?.method);
      setValue('ref', payment?.ref);
      setValue('observation', payment?.observation);
      if (payment?.bank) {
        setValue('bank', payment?.bank);
      }
      if (payment?.phone) {
        setValue('phone', payment?.phone);
      }
      if (payment?.identificationType) {
        setValue('identificationType', payment?.identificationType);
      }
      if (payment?.identification) {
        setValue('identification', payment?.identification);
      }
      if (payment?.date) {
        setValue('date', new Date(payment?.date));
      }
    } else {
      reset();
    }
  };

  const appendFile = (data) => {
    const formData = new FormData();
    formData.append('paid', data?.paid);
    formData.append('amount', data?.amount);
    formData.append('amountBs', data?.amountBs);
    formData.append('method', data?.method);
    formData.append('ref', data?.ref);
    formData.append('date', data?.date);
    formData.append('observation', data?.observation);

    if (data?.bank) {
      formData.append('bank', data?.bank);
    }
    if (data?.phone) {
      formData.append('phone', data?.phone);
    }
    if (data?.identificationType) {
      formData.append('identificationType', data?.identificationType);
    }
    if (data?.identification) {
      formData.append('identification', data?.identification);
    }
    if (fileUploaded) {
      formData.append('paymentEvidence', fileUploaded);
    }
    return formData;
  };

  useEffect(() => {
    getDollarRate();
    getPaymentMethods();
  }, [open, membership]);

  const editPayment = handleSubmit(async (data) => {
    let totalBill = 0;

    for (let i = 0; i < membership?.payments.length; i++) {
      totalBill = totalBill + membership?.payments[i]?.totalAmount;
    }

    const paymentId = openEditPaymentModal?.payment?.payments?.[0];

    const paymentBill =
      membership?.payments.find((p) => p._id === paymentId)?.totalAmount ?? 0;
    if (
      [paymentType.FINANCIADO, paymentType.CASHEA].includes(
        membership?.paymentType
      )
    ) {
      if (data?.paid - paymentBill < Number(data?.amount)) {
        notify('El monto pagado no puede ser menor al monto recibido', 'info');
        // notify('El monto pagado no puede ser menor al monto recibido', 'error');
        return;
      }
    } else {
      if (data?.paid < totalBill) {
        notify('El monto pagado no puede ser menor al monto recibido', 'error');
        return;
      }
    }

    let fileToPost = appendFile(data);

    if (!isLoading) {
      setIsLoading(true);
      paymentsService
        .editCash(fileToPost, openEditPaymentModal?.payment?._id)
        .then((response) => {
          if ([200, 201].includes(response.status)) {
            notify(`Pago editado con exito`, 'success');
            handleEditChangeModal();
            reloadMembership();
            setIsLoading(false);
          } else {
            notify(`${response.message}`, 'error');
            setIsLoading(false);
          }
        })
        .catch((error) => {
          setIsLoading(false);
        });
    }
  });

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();

      reader.onload = () => {
        // Do whatever you want with the file contents
      };
      reader.readAsArrayBuffer(file);

      if (
        !['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'].includes(
          file.type
        )
      ) {
        notify('Agregar un formato valido de imagen', 'error');
        return null;
      }
      let maxWeight = 5;
      if (file.size * 0.000001 > maxWeight) {
        notify(`La imagen no debe superar los ${maxWeight} MB`, 'error');
      } else {
        setFileUploaded(file);
      }
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });
  const [fileUploaded, setFileUploaded] = useState('');

  if (membership?.paymentChanges?.length === 0) {
    return null;
  }

  const totalPaidChanges = (membership?.paymentChanges ?? []).reduce(
    (total, change) => total + (Number(change?.paid) || 0),
    0
  );

  const shouldDisableReferenceAndReceipt = (paymentMethod) => {
    const role = authService?.getUserType();
    const hunter = salesRoles.SALES_HUNTER;
    const farmer = salesRoles.SALES_FARMER;
    const counter = salesRoles.SALES_COUNTER;

    const notAllowedRoles = [hunter, farmer, counter];

    // Nombres de métodos de pago a bloquear
    const blockedMethodNames = [
      'PAGO MÓVIL BANCAMIGA',
      'PAGO MÓVIL BNC',
      'PAGO MÓVIL MERCANTIL',
      'PAGO MÓVIL BANESCO',
      'PAGO MOVIL BANPLUS',
      'PAGO MÓVIL GENERICO',
      'PAGO MÓVIL',
      'C2P BANCAMIGA',
      'TRANSFERENCIA BANCAMIGA',
      'TRANSFERENCIA BNC',
      'TRANSFERENCIA MERCANTIL',
      'TRANSFERENCIA BANESCO',
      'TRANSFERENCIA BANPLUS',
      'TRANSFERENCIA GENERICO',
      'BANK TRANSFER',
      'ZELLE',
      'PAYPAL',
      'BINANCE',
    ];

    const selectedMethod = paymentMethods.find((p) => p._id === paymentMethod);
    const isBlocked = selectedMethod
      ? blockedMethodNames.includes(selectedMethod.name)
      : blockedMethodNames.includes(paymentMethod);

    // Si el rol es hunter, farmer o counter (interno o externo)
    // y el método de pago es uno de los bloqueados
    if (notAllowedRoles.includes(role?.code || role) && isBlocked) {
      return true;
    }
    return false;
  };

  return (
    <>
      <LoadSpinner show={isLoading} />
      <EditChangeModal
        modal={openEditPaymentModal}
        register={register}
        action={editPayment}
        handleClose={() => handleEditChangeModal()}
        errors={errors}
        setValue={setValue}
        control={control}
        options={{
          paymentMethods: paymentMethods,
          paymentTypesOptions: paymentTypesOptions,
          dollarRate: dollarRate,
        }}
        watch={watch}
        getValues={getValues}
        fileUploaded={fileUploaded}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        setFileUploaded={setFileUploaded}
        shouldDisableReferenceAndReceipt={shouldDisableReferenceAndReceipt}
      />

      <div className="order-card">
        <OderCardHeader open={open} setOpen={setOpen} label={label} />
        <h6 style={{ marginTop: '12px' }}>
          Monto Recibido: {totalPaidChanges}$
        </h6>

        {open && (
          <>
            <CashTable
              role={authService?.getUserType()}
              header={ChangeTableHeader}
              membership={membership}
              handleEditChangeModal={handleEditChangeModal}
            />
          </>
        )}
      </div>
    </>
  );
};
